import { useEffect, useState } from "react";
import { Modal, Button, Table, Stack, Form } from "react-bootstrap";
import { getNotasOferta, createNotaOferta } from "../../services/notas";
import { CiWarning } from "react-icons/ci";
import { LuPlus, LuX } from "react-icons/lu";

function NotasOferta({ oferta, show, close, handleClose, onClose, user }) {
  const [loading, setLoading] = useState(false);
  const [notas, setNotas] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [creating, setCreating] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);

  // Support both 'close' and 'handleClose' props for compatibility
  const closeHandler = handleClose || close;

  // All authenticated users can create notes
  const canCreateNotes = !!user;

  useEffect(() => {
    if (show && oferta) {
      fetchNotas();
    }
  }, [show, oferta]);

  const fetchNotas = async () => {
    setLoading(true);
    const res = await getNotasOferta(oferta.id);
    setNotas(res);
    setLoading(false);
  };

  const closeDialog = () => {
    setShowCreateForm(false);
    setNoteText("");
    setSelectedNote(null);
    if (typeof closeHandler === 'function') {
      closeHandler(false);
    }
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const handleCreateNote = async () => {
    if (!noteText.trim()) {
      return;
    }

    const userName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : "";
    const prefix = userName || (user?.is_superadmin || user?.is_internal_agent || user?.is_call_agent ? "Backoffice" : "Agente");
    const noteWithUser = `${prefix}: ${noteText}`;

    setCreating(true);
    try {
      await createNotaOferta(oferta.id, noteWithUser);
      setNoteText("");
      setShowCreateForm(false);
      // Refresh the notes list
      await fetchNotas();
    } catch (err) {
      // Error is already handled by the service
    } finally {
      setCreating(false);
    }
  };

  const getNoteAuthorAndText = (rawNote) => {
    const safeNote = String(rawNote || "");
    const separatorIndex = safeNote.indexOf(":");

    if (separatorIndex <= 0) {
      return {
        author: "Agente",
        text: safeNote,
      };
    }

    const author = safeNote.slice(0, separatorIndex).trim() || "Agente";
    const text = safeNote.slice(separatorIndex + 1).trim();

    return {
      author,
      text: text || safeNote,
    };
  };

  return (
    <>
      <Modal
        show={show}
        size="lg"
        backdrop="static"
        centered
        contentClassName="border-0 rounded-4 shadow-lg"
        onHide={closeDialog}
      >
        <Modal.Header className="bg-white border-bottom pb-3">
          <div className="d-flex justify-content-between align-items-center w-100">
            <div className="d-flex flex-column">
              <Modal.Title className="fw-bold text-primary mb-0">Notas de la oferta</Modal.Title>
              <span className="text-muted small">Nº {oferta?.name || ''}</span>
            </div>
            <Stack direction="horizontal" gap={2}>
              {!showCreateForm && canCreateNotes && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowCreateForm(true)}
                  className="rounded-pill px-3"
                >
                  <LuPlus className="me-1" /> Crear Nota
                </Button>
              )}
              <Button
                variant="link"
                className="text-secondary p-0"
                onClick={closeDialog}
                style={{ fontSize: '1.5rem', lineHeight: 1 }}
              >
                <LuX />
              </Button>
            </Stack>
          </div>
        </Modal.Header>
        <Modal.Body className="p-4">
          {showCreateForm && (
            <div className="mb-4 p-3 bg-light rounded-3">
              <h6 className="fw-bold mb-3">Nueva Nota</h6>
              <Form.Group className="mb-3">
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Escribe tu nota aquí..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  disabled={creating}
                  autoFocus
                />
              </Form.Group>
              <Stack direction="horizontal" gap={2}>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleCreateNote}
                  disabled={creating || !noteText.trim()}
                  className="rounded-pill px-4"
                >
                  {creating ? "Guardando..." : "Guardar"}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNoteText("");
                  }}
                  disabled={creating}
                  className="rounded-pill px-4"
                >
                  Cancelar
                </Button>
              </Stack>
            </div>
          )}

          {loading ? (
            <div
              style={{ height: "40vh" }}
              className="d-flex flex-column justify-content-center align-items-center text-muted"
            >
              <div className="spinner-border text-primary mb-3" role="status"></div>
              <h5 className="fw-bold">Cargando notas...</h5>
            </div>
          ) : notas && notas.length > 0 ? (
            <div
              style={{ height: "40vh", overflowY: "auto" }}
              className="rounded-3 border border-light shadow-sm"
            >
              <Table
                striped
                hover
                borderless
                className="mb-0"
              >
                <thead className="bg-light sticky-top">
                  <tr>
                    <th className="py-3 px-4 text-muted small fw-bold text-uppercase">Fecha</th>
                    <th className="py-3 px-4 text-muted small fw-bold text-uppercase">Nota</th>
                  </tr>
                </thead>
                <tbody>
                  {notas.map((nota) => {
                    const { author, text } = getNoteAuthorAndText(nota.name);

                    return (
                      <tr key={nota.id} className="align-middle">
                        <td className="px-4 py-3 text-nowrap text-secondary fw-bold" style={{ width: '140px' }}>{nota.date_event}</td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            className="btn btn-link p-0 text-start text-decoration-none text-secondary w-100"
                            onClick={() => setSelectedNote(nota)}
                            style={{ whiteSpace: 'normal' }}
                          >
                            <div className="small fw-bold text-primary mb-1">{author}</div>
                            <div
                              style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {text}
                            </div>
                            <div className="small text-primary fw-semibold mt-1">Ver nota completa</div>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          ) : (
            <div style={{ height: "40vh" }} className="d-flex flex-column justify-content-center align-items-center text-center opacity-75">
              <CiWarning className="display-1 text-muted mb-3" />
              <h4 className="text-muted fw-bold">No hay notas para esta oferta.</h4>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={!!selectedNote}
        onHide={() => setSelectedNote(null)}
        centered
        contentClassName="border-0 rounded-4 shadow-lg"
      >
        <Modal.Header closeButton className="bg-white border-bottom-0 pb-0">
          {selectedNote ? (() => {
            const { author } = getNoteAuthorAndText(selectedNote.name);
            return (
              <div>
                <Modal.Title className="fw-bold text-primary">Nota completa</Modal.Title>
                <div className="small text-primary fw-bold mt-1">{author}</div>
                <div className="small text-muted mt-1">{selectedNote?.date_event || ''}</div>
              </div>
            );
          })() : (
            <div>
              <Modal.Title className="fw-bold text-primary">Nota completa</Modal.Title>
              <div className="small text-muted mt-1">{selectedNote?.date_event || ''}</div>
            </div>
          )}
        </Modal.Header>
        <Modal.Body className="pt-3">
          {selectedNote ? (() => {
            const { text } = getNoteAuthorAndText(selectedNote.name);
            return (
              <div
                className="rounded-3 bg-light p-3 text-secondary"
                style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '60vh', overflowY: 'auto' }}
              >
                {text}
              </div>
            );
          })() : (
            <div
              className="rounded-3 bg-light p-3 text-secondary"
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '60vh', overflowY: 'auto' }}
            >
              {selectedNote?.name || ''}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}

export default NotasOferta;